import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import styled from 'styled-components';
import { resendVerification } from '../../../services/authService';

const FormContainer = styled.div`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #4b5563;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #357abd;
  }
  
  &:disabled {
    background-color: #a0c4ff;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  margin-bottom: 1rem;
  text-align: center;
`;

const SuccessMessage = styled.p`
  color: #2e7d32;
  margin-bottom: 1rem;
  text-align: center;
`;

const FormFooter = styled.div`
  margin-top: 1rem;
  text-align: center;
  color: #4b5563;
  
  a {
    color: #4a90e2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [resending, setResending] = useState(false);
  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromRegistration = location.state && location.state.fromRegistration;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error and any unverified state when user starts typing
    if (error) clearError();
    if (unverifiedEmail) {
      setUnverifiedEmail(null);
      setVerificationMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await login(formData.email, formData.password, keepMeLoggedIn);
      const user = response?.user;

      // Redirect to dashboard after successful login
      if (user) {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);

      // Handle unverified email specially so we can offer resend
      if (err?.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(formData.email);
        setVerificationMessage(err.message || 'Please verify your email before logging in.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;

    try {
      setResending(true);
      setVerificationMessage('Sending verification email...');
      await resendVerification(unverifiedEmail);
      setVerificationMessage('Verification email sent. Please check your inbox (and spam folder).');
    } catch (err) {
      console.error('Resend verification error:', err);
      setVerificationMessage(err?.message || 'Could not resend verification email. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <FormContainer>
      <FormTitle>Welcome Back</FormTitle>
      
      {fromRegistration && (
        <SuccessMessage>Your account has been created. Please log in.</SuccessMessage>
      )}

      {error && !unverifiedEmail && <ErrorMessage>{error}</ErrorMessage>}

      {unverifiedEmail && (
        <>
          <ErrorMessage>{verificationMessage || 'Please verify your email before logging in.'}</ErrorMessage>
          <FormGroup>
            <Button type="button" disabled={resending} onClick={handleResendVerification}>
              {resending ? 'Resending verification email...' : 'Resend verification email'}
            </Button>
          </FormGroup>
        </>
      )}
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
            placeholder="Enter your email"
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="current-password"
            placeholder="Enter your password"
          />
        </FormGroup>
        <CheckboxRow>
          <input
            type="checkbox"
            id="keepMeLoggedIn"
            checked={keepMeLoggedIn}
            onChange={e => setKeepMeLoggedIn(e.target.checked)}
          />
          <label htmlFor="keepMeLoggedIn">Keep me logged in</label>
        </CheckboxRow>
        
        <FormGroup>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Logging in...' : 'Log In'}
          </Button>
        </FormGroup>
      </form>
      
      <FormFooter>
        <p>
          Don't have an account?{' '}
          <Link to="/register" onClick={clearError}>Sign up</Link>
        </p>
        <p>
          <Link to="/forgot-password">Forgot your password?</Link>
        </p>
      </FormFooter>
    </FormContainer>
  );
};

export default LoginForm;
